import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, TextField, IconButton, Chip, CircularProgress, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, LinearProgress,
} from '@mui/material';
import { Search as SearchIcon, Download as DownloadIcon } from '@mui/icons-material';
import {
  fetchLeaveUsageReport, fetchLeaveTypes, fetchLeavePeriods,
} from '../../store/slices/leaveSlice';
import { AppDispatch, RootState } from '../../store/store';

const LeaveUsageReport: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { usageReport, leaveTypes, leavePeriods, loading } = useSelector((state: RootState) => state.leave);

  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  useEffect(() => {
    dispatch(fetchLeaveTypes());
    dispatch(fetchLeavePeriods());
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const loadReport = () => {
    dispatch(fetchLeaveUsageReport({
      leavePeriodId: periodFilter || undefined,
      searchTerm: searchTerm || undefined,
    }));
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodFilter]);

  const handleSearch = () => loadReport();

  // Get unique leave type names from the report data
  const allLeaveTypeNames = Array.from(
    new Set(usageReport.flatMap(r => r.leaveUsage.map(u => u.leaveTypeName)))
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Leave Usage Report</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField size="small" placeholder="Search employee..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} size="small"><SearchIcon /></IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Leave Period</InputLabel>
            <Select value={periodFilter} label="Leave Period" onChange={(e) => setPeriodFilter(e.target.value)}>
              <MenuItem value="">All Periods</MenuItem>
              {leavePeriods.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Summary Cards */}
      {usageReport.length > 0 && (
        <>
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Total Employees</Typography>
            <Typography variant="h4" fontWeight={700}>{usageReport.length}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Total Entitled</Typography>
            <Typography variant="h4" fontWeight={700} color="primary">
              {usageReport.reduce((s, r) => s + r.totalEntitled, 0)}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Total Carry Forward</Typography>
            <Typography variant="h4" fontWeight={700} color="info.main">
              {usageReport.reduce((s, r) => s + r.totalCarryForward, 0)}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Total Used</Typography>
            <Typography variant="h4" fontWeight={700} color="warning.main">
              {usageReport.reduce((s, r) => s + r.totalUsed, 0)}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Total Remaining</Typography>
            <Typography variant="h4" fontWeight={700} color="success.main">
              {usageReport.reduce((s, r) => s + r.totalRemaining, 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, mt: 0.5, display: 'block' }}>
              (Entitled + CF - Used)
            </Typography>
          </Paper>
        </Box>
        <Paper sx={{ p: 1.5, mb: 2, backgroundColor: '#f0f7ff', borderLeft: '4px solid #1976d2' }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Formula:</strong> Total Remaining = Total Entitled + Total Carry Forward - Total Used
          </Typography>
        </Paper>
        </>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Total Records: {usageReport.length}
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Employee</strong></TableCell>
                <TableCell><strong>Department</strong></TableCell>
                {allLeaveTypeNames.map(name => (
                  <TableCell key={name} align="center"><strong>{name}</strong></TableCell>
                ))}
                <TableCell align="center"><strong>Total Entitled</strong></TableCell>
                <TableCell align="center"><strong>Total Carry Fwd</strong></TableCell>
                <TableCell align="center"><strong>Total Used</strong></TableCell>
                <TableCell align="center"><strong>Total Remaining</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usageReport.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4 + allLeaveTypeNames.length + 3} align="center">
                    No data found. Select a leave period and ensure entitlements exist.
                  </TableCell>
                </TableRow>
              ) : (
                usageReport.map((report) => (
                  <TableRow key={report.employeeId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{report.employeeName}</Typography>
                      <Typography variant="caption" color="text.secondary">{report.employeeCode}</Typography>
                    </TableCell>
                    <TableCell>{report.departmentName || '-'}</TableCell>
                    {allLeaveTypeNames.map(name => {
                      const usage = report.leaveUsage.find(u => u.leaveTypeName === name);
                      return (
                        <TableCell key={name} align="center">
                          {usage ? (
                            <Box>
                              <Typography variant="caption">
                                {usage.used}/{usage.entitled + usage.carryForward}
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={
                                  (usage.entitled + usage.carryForward) > 0
                                    ? Math.min(100, (usage.used / (usage.entitled + usage.carryForward)) * 100)
                                    : 0
                                }
                                color={usage.remaining > 0 ? 'primary' : 'error'}
                                sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                              />
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">
                      <Chip label={report.totalEntitled} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={report.totalCarryForward} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={report.totalUsed} size="small" color="warning" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={report.totalRemaining} size="small"
                        color={report.totalRemaining > 0 ? 'success' : 'error'} variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}
    </Box>
  );
};

export default LeaveUsageReport;
